import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Fab,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  EventAvailable,
  Assessment,
  Phone,
  Email,
  LinkedIn,
  Edit,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { useAuth } from '../lib/supabase/AuthContext';
import {
  getAllCampaigns,
  getActiveCampaigns,
  createRdv,
  getCallerRdv,
  updateRdv,
  deleteRdv,
  getMyCallerStats,
  searchLeads,
} from '../lib/supabase/callerService';
import { RdvFormData, RdvWithDetails } from '../types/admin';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`caller-tabpanel-${index}`}
      aria-labelledby={`caller-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

export const CallerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // États pour les données
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [rdvList, setRdvList] = useState<RdvWithDetails[]>([]);
  const [callerStats, setCallerStats] = useState<any>(null);

  // États pour le formulaire RDV
  const [rdvDialogOpen, setRdvDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRdv, setSelectedRdv] = useState<RdvWithDetails | null>(null);
  const [rdvForm, setRdvForm] = useState<RdvFormData>({
    prenom: '',
    nom: '',
    entreprise: '',
    email: '',
    telephone: '',
    rdv_date: '',
    rdv_commentaire: '',
    rdv_source: 'Phone',
    campaign_id: '',
    etat_rdv: 'En attente',
    prospection_etape: 'RDV planifié',
  });

  // États pour les filtres
  const [rdvFilter, setRdvFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // États pour l'autocomplétion
  const [leadSuggestions, setLeadSuggestions] = useState<any[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  // États pour la modale retour client
  const [retourClientDialogOpen, setRetourClientDialogOpen] = useState(false);
  const [currentRetourClient, setCurrentRetourClient] = useState<string>('');

  // Charger les données
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const [campaigns, rdvs, stats] = await Promise.all([
        getAllCampaigns(),
        getCallerRdv(user.id),
        getMyCallerStats(user.id),
      ]);

      setCampaigns(campaigns || []);
      setRdvList(rdvs || []);
      setCallerStats(stats);
      console.log('Campagnes chargées:', campaigns);
    } catch (err: any) {
      console.error('Erreur lors du chargement des données caller:', err);
      setError(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Filtrer les RDV
  const filteredRdv = rdvList.filter(rdv => {
    const matchesFilter = rdvFilter === 'all' || rdv.etat_rdv === rdvFilter;
    const matchesSearch = 
      rdv.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rdv.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rdv.entreprise.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rdv.campaign_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  // Gérer le formulaire RDV
  const handleRdvFormChange = (field: keyof RdvFormData, value: string) => {
    setRdvForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetRdvForm = () => {
    setRdvForm({
      prenom: '',
      nom: '',
      entreprise: '',
      email: '',
      telephone: '',
      rdv_date: '',
      rdv_commentaire: '',
      rdv_source: 'Phone',
      campaign_id: '',
      etat_rdv: 'En attente',
      prospection_etape: 'RDV planifié',
    });
    setIsEditMode(false);
    setSelectedRdv(null);
    resetAutocompletion();
  };

  const handleCreateRdv = () => {
    resetRdvForm();
    setRdvDialogOpen(true);
  };

  const handleEditRdv = (rdv: RdvWithDetails) => {
    setRdvForm({
      prenom: rdv.prenom,
      nom: rdv.nom,
      entreprise: rdv.entreprise,
      email: rdv.email || '',
      telephone: rdv.telephone || '',
      rdv_date: rdv.rdv_date,
      rdv_commentaire: rdv.rdv_commentaire || '',
      rdv_source: rdv.rdv_source,
      campaign_id: rdv.campaign_id,
      etat_rdv: rdv.etat_rdv,
      prospection_etape: rdv.prospection_etape,
    });
    setSelectedRdv(rdv);
    setSelectedLeadId(rdv.id);
    setIsEditMode(true);
    setRdvDialogOpen(true);
  };

  const handleSaveRdv = async () => {
    if (!user) return;

    try {
      console.log('handleSaveRdv - isEditMode:', isEditMode);
      console.log('handleSaveRdv - selectedRdv:', selectedRdv);
      console.log('handleSaveRdv - rdvForm:', rdvForm);
      console.log('handleSaveRdv - selectedLeadId:', selectedLeadId);

      if (isEditMode && selectedRdv) {
        // Mode édition
        console.log('Mode édition - Appel updateRdv avec ID:', selectedRdv.id);
        await updateRdv(selectedRdv.id, rdvForm);
      } else {
        // Mode création - passer l'ID du lead s'il est sélectionné
        console.log('Mode création - Appel createRdv');
        await createRdv(rdvForm, user.id, selectedLeadId || undefined);
      }

      setRdvDialogOpen(false);
      resetRdvForm();
      
      // Recharger les RDV
      console.log('Rechargement des RDV...');
      const updatedRdv = await getCallerRdv(user.id);
      setRdvList(updatedRdv);
      console.log('RDV rechargés:', updatedRdv);
    } catch (err: any) {
      console.error('Erreur lors de la sauvegarde du RDV:', err);
      setError(`Erreur: ${err.message}`);
    }
  };

  const handleDeleteRdv = async (rdvId: string) => {
    try {
      await deleteRdv(rdvId);
      await loadAllData(); // Recharger la liste
    } catch (err: any) {
      console.error('Erreur lors de la suppression du RDV:', err);
      setError(`Erreur: ${err.message}`);
    }
  };

  // Fonctions pour l'autocomplétion
  const handleSearchLeads = async (searchValue: string) => {
    if (searchValue.length < 2) {
      setLeadSuggestions([]);
      return;
    }

    try {
      setIsLoadingSuggestions(true);
      const suggestions = await searchLeads(searchValue);
      setLeadSuggestions(suggestions);
    } catch (err: any) {
      console.error('Erreur lors de la recherche de leads:', err);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleSelectLead = (lead: any) => {
    if (lead) {
      console.log('Lead sélectionné:', lead);
      console.log('Campagne du lead:', lead.campaigns);
      setSelectedLead(lead);
      setSelectedLeadId(lead.id);
      setRdvForm(prev => ({
        ...prev,
        prenom: lead.prenom,
        nom: lead.nom,
        entreprise: lead.entreprise,
        email: lead.email || '',
        telephone: lead.telephone || '',
        campaign_id: lead.campaigns?.id || '',
      }));
    }
  };

  const resetAutocompletion = () => {
    setSelectedLead(null);
    setSelectedLeadId(null);
    setLeadSuggestions([]);
  };

  // Fonction pour ouvrir la modale retour client
  const handleViewRetourClient = (retourClient: string | null) => {
    setCurrentRetourClient(retourClient || 'Aucun retour client');
    setRetourClientDialogOpen(true);
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Interface Caller
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Mes Statistiques" icon={<Assessment />} />
          <Tab label="Mes RDV" icon={<EventAvailable />} />
        </Tabs>
      </Box>

      {/* Tab 1: Statistiques */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  RDV Générés
                </Typography>
                <Typography variant="h4">
                  {callerStats?.totalRdvGenerated || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  RDV Validés
                </Typography>
                <Typography variant="h4" color="primary">
                  {callerStats?.rdvValidated || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  En Attente
                </Typography>
                <Typography variant="h4" color="success.main">
                  {callerStats?.rdvPending || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Taux Validation
                </Typography>
                <Typography variant="h4" color="info.main">
                  {callerStats?.validationRate?.toFixed(1) || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Moyenne par jour: {callerStats?.avgRdvPerDay?.toFixed(1) || 0} RDV
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Objectif mensuel: 50 RDV
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Campagnes actives
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {campaigns.length} campagnes disponibles
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 2: Mes RDV */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Rechercher"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={rdvFilter}
                label="Statut"
                onChange={(e) => setRdvFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="En attente">En attente</MenuItem>
                <MenuItem value="Validé">Validé</MenuItem>
                <MenuItem value="Litige">Litige</MenuItem>
                <MenuItem value="No show">No show</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateRdv}
          >
            Nouveau RDV
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date RDV</TableCell>
                <TableCell>Prospect</TableCell>
                <TableCell>Entreprise</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Campagne</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Retour client</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRdv.map((rdv) => (
                <TableRow key={rdv.id}>
                  <TableCell>
                    {new Date(rdv.rdv_date).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {rdv.prenom} {rdv.nom}
                    </Typography>
                  </TableCell>
                  <TableCell>{rdv.entreprise}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {rdv.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Email fontSize="small" />
                          {rdv.email}
                        </Box>
                      )}
                      {rdv.telephone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" />
                          {rdv.telephone}
                        </Box>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {rdv.campaign_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {rdv.client_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rdv.rdv_source}
                      size="small"
                      icon={
                        rdv.rdv_source === 'Mail' ? <Email /> :
                        rdv.rdv_source === 'LinkedIn' ? <LinkedIn /> : <Phone />
                      }
                      color={
                        rdv.rdv_source === 'Mail' ? 'primary' :
                        rdv.rdv_source === 'LinkedIn' ? 'info' : 'secondary'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rdv.etat_rdv}
                      color={
                        rdv.etat_rdv === 'Validé' ? 'success' :
                        rdv.etat_rdv === 'En attente' ? 'warning' :
                        rdv.etat_rdv === 'Litige' ? 'error' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {rdv.retour_client ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewRetourClient(rdv.retour_client)}
                      >
                        Voir
                      </Button>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Aucun retour
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Modifier">
                      <IconButton size="small" onClick={() => handleEditRdv(rdv)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Bouton flottant pour créer un RDV */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleCreateRdv}
      >
        <Add />
      </Fab>

      {/* Dialog pour créer/modifier un RDV */}
      <Dialog open={rdvDialogOpen} onClose={() => setRdvDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Modifier le RDV' : 'Nouveau RDV'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={leadSuggestions}
                  loading={isLoadingSuggestions}
                  value={selectedLead}
                  onChange={(_, newValue) => {
                    handleSelectLead(newValue);
                  }}
                  onInputChange={(_, newInputValue) => {
                    handleSearchLeads(newInputValue);
                    // Si on efface tout, on reset
                    if (!newInputValue) {
                      setSelectedLead(null);
                      setRdvForm(prev => ({ ...prev, prenom: '' }));
                    }
                    // Si on tape et qu'aucun lead n'est sélectionné, on met à jour juste le prénom
                    else if (!selectedLead) {
                      setRdvForm(prev => ({ ...prev, prenom: newInputValue }));
                    }
                  }}
                  getOptionLabel={(option) => 
                    typeof option === 'string' ? option : `${option.prenom} ${option.nom} (${option.entreprise})`
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {option.prenom} {option.nom}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.entreprise}
                        </Typography>
                        {option.email && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            📧 {option.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Prénom *"
                      placeholder="Commencez à taper pour rechercher..."
                    />
                  )}
                  freeSolo
                  clearOnBlur={false}
                  blurOnSelect
                  inputValue={rdvForm.prenom}
                />
                {selectedLead && (
                  <Tooltip title="Nouveau prospect">
                    <IconButton 
                      onClick={() => {
                        setSelectedLead(null);
                        setSelectedLeadId(null);
                        setRdvForm(prev => ({ 
                          ...prev, 
                          prenom: '', 
                          nom: '', 
                          entreprise: '', 
                          email: '', 
                          telephone: '',
                          campaign_id: ''
                        }));
                      }}
                      size="small"
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom *"
                value={rdvForm.nom}
                onChange={(e) => handleRdvFormChange('nom', e.target.value)}
                helperText={selectedLead ? "Auto-rempli depuis la sélection (modifiable)" : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Entreprise *"
                value={rdvForm.entreprise}
                onChange={(e) => handleRdvFormChange('entreprise', e.target.value)}
                helperText={selectedLead ? "Auto-rempli depuis la sélection (modifiable)" : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={rdvForm.email}
                onChange={(e) => handleRdvFormChange('email', e.target.value)}
                helperText={selectedLead ? "Auto-rempli depuis la sélection (modifiable)" : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={rdvForm.telephone}
                onChange={(e) => handleRdvFormChange('telephone', e.target.value)}
                helperText={selectedLead ? "Auto-rempli depuis la sélection (modifiable)" : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date et heure du RDV *"
                type="datetime-local"
                value={rdvForm.rdv_date}
                onChange={(e) => handleRdvFormChange('rdv_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Source *</InputLabel>
                <Select
                  value={rdvForm.rdv_source}
                  label="Source *"
                  onChange={(e) => handleRdvFormChange('rdv_source', e.target.value)}
                >
                  <MenuItem value="Phone">Téléphone</MenuItem>
                  <MenuItem value="Mail">Email</MenuItem>
                  <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Campagne *</InputLabel>
                <Select
                  value={rdvForm.campaign_id}
                  label="Campagne *"
                  onChange={(e) => handleRdvFormChange('campaign_id', e.target.value)}
                  disabled={!!selectedLead && !isEditMode}
                >
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.nom} {!campaign.lance ? '(Inactive)' : ''}
                    </MenuItem>
                  ))}
                </Select>
                {selectedLead && !isEditMode && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Auto-rempli depuis la sélection
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Commentaire"
                value={rdvForm.rdv_commentaire}
                onChange={(e) => handleRdvFormChange('rdv_commentaire', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRdvDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveRdv}
            disabled={!rdvForm.prenom || !rdvForm.nom || !rdvForm.entreprise || !rdvForm.rdv_date || !rdvForm.campaign_id}
          >
            {isEditMode ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour afficher le retour client */}
      <Dialog 
        open={retourClientDialogOpen} 
        onClose={() => setRetourClientDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Retour Client
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
            {currentRetourClient}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetourClientDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}; 